import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import moment from "moment";
import {
    type I360PhotoSlide,
    type IBaseSlide,
    type IVideoSlide,
    type IVimeoSlide,
} from "yet-another-react-lightbox";
import { type Slide } from "yet-another-react-lightbox";

import { getSnapshotAppDefaultInfo } from "helpers/globalContext/globalInfoSnapshot";

import { FileStatus, FolderTypes, MediaType } from "types/enum";

import { isNullOrUndefined } from "utilities/object/object";

import { type IMediaData } from "commonComponents/utilities/MediaModal/common/types";

import { getViewerSource, isPhotoFileType } from "entity/media/common/media.utils";

export const isVimeoVideoSlide = (slide: Slide): slide is IVimeoSlide =>
    slide.type === "custom-video";
export const is360PhotoSlide = (slide: Slide): slide is I360PhotoSlide =>
    slide.type === "custom-360-photo";

export const canViewBTPhotoInLightbox = (file: BTFileSystem) =>
    file.isPhoto ||
    file.mediaType === MediaType.Photo ||
    (file.extension && isPhotoFileType(file.extension)) ||
    file.is360Media;

export const canViewBTVideoInLightbox = (file: BTFileSystem) => {
    const nativeFile = file.nativeFile;
    if (file.mediaType === MediaType.Video && nativeFile !== undefined) {
        return true;
    } else if (
        file.mediaType === MediaType.Video &&
        file.videoId !== undefined &&
        file.videoId !== null &&
        file.fileStatus === FileStatus.Available
    ) {
        return true;
    } else if (
        file.mediaType === MediaType.Video &&
        !file.isTempFile &&
        file.docPath &&
        file.fileStatus === FileStatus.Available
    ) {
        return true;
    } else {
        return false;
    }
};

export const canViewBTFileSystemInLightbox = (file: BTFileSystem) => {
    if (!isNullOrUndefined(file.folderType) && file.folderType !== FolderTypes.NotAFolder) {
        return false;
    }

    return canViewBTPhotoInLightbox(file) || canViewBTVideoInLightbox(file);
};

/**
 * `BTFileSystem.dateAttached` is typed `moment.Moment`, but the constructor assigns the raw API
 * value (ISO string, `Date`, or null/undefined) without wrapping it - normalize here rather than
 * trusting the type.
 */
const normalizeDateAttached = (dateAttached: BTFileSystem["dateAttached"]) =>
    dateAttached ? moment(dateAttached) : undefined;

export const btFileSystemToSlide = (file: BTFileSystem): IBaseSlide => {
    if (file.is360Media) {
        return {
            type: "custom-360-photo",
            metadata: {
                mediaId: file.id,
                mainFile: file.viewingPermissions?.mainFile,
                dateAttached: normalizeDateAttached(file.dateAttached),
                addedBy: file.addedBy,
            },
            src: file.docPath,
            title: file.title,
            download: {
                url: file.docPath,
                filename: file.title,
            },
        };
    }

    if (file.mediaType === MediaType.Video) {
        // Function version is needed because the CFV converts to observable.
        // We should be able to remove the function version of 'nativeFile' with the CFV.
        const nativeFile = file.nativeFile;
        if (nativeFile) {
            const blobUrl = URL.createObjectURL(nativeFile);
            const slide: IVideoSlide = {
                type: "video",
                metadata: {
                    mediaId: file.id ?? file.videoId,
                    jobId: file.jobsiteId,
                    mainFile: file.viewingPermissions?.mainFile,
                    dateAttached: normalizeDateAttached(file.dateAttached),
                    addedBy: file.addedBy,
                },
                sources: [
                    {
                        src: blobUrl,
                        type: nativeFile.type,
                    },
                    // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/video
                    // Attempt to play the video if the format is not supported
                    // eg. video/quicktime in Chrome
                    {
                        src: blobUrl,
                        type: "",
                    },
                ],
                src: blobUrl,
                title: file.fileName,
                download: FileStatus.Available === file.fileStatus,
                autoPlay: true,
            };
            return slide;
        }

        const slide: IVimeoSlide = {
            type: "custom-video",
            metadata: {
                mediaId: file.id,
                jobId: file.jobsiteId,
                mainFile: file.viewingPermissions?.mainFile,
                dateAttached: normalizeDateAttached(file.dateAttached),
                addedBy: file.addedBy,
            },
            vimeoId: file.videoId,
            src: file.docPath,
            title: file.title,
            download: FileStatus.Available === file.fileStatus,
        };
        return slide;
    }

    return {
        type: "image",
        metadata: {
            mediaId: file.id,
            mainFile: file.viewingPermissions?.mainFile,
            dateAttached: normalizeDateAttached(file.dateAttached),
            addedBy: file.addedBy,
        },
        src: browserSafeImageSrc(
            file.hasAnnotations ? file.annotatedDocPath : file.docPath,
            file.extension ?? file.title ?? "",
            file.hasAnnotations ? file.previewAnnotatedDocPath : file.previewDocPath,
        ),
        title: file.title,
        download: {
            url: file.hasAnnotations ? file.annotatedDocPath : file.docPath,
            filename: file.title,
        },
    };
};

// Only the document file routes (`/api/files/{id}`, `/api/files/{token}/shared`) have a preview twin; signature,
// subcertificate and other `/api/files/...` routes must pass through untouched.
const fileRoutePattern = /^(.*\/api\/files\/(?:\d+|[^/?]+(?=\/shared)))(\/shared)?(\?.*)?$/i;

/**
 * Display-only; downloads keep the original. Under webpImageDelivery the slide shows the cached, size-capped preview
 * (WebP where supported) rather than the full camera original. Otherwise a HEIC asks the file route for a JPEG, since
 * HEICs uploaded while the flag was on stay HEIC after it is turned off.
 */
function browserSafeImageSrc<T extends string | undefined>(
    url: T,
    fileNameOrExtension: string | null | undefined,
    previewUrl?: string | null,
): T {
    if (!url) {
        return url;
    }
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- read outside React, like getImageUrlFromDocInstanceId; tracked by 282446
    if (getSnapshotAppDefaultInfo()?.webpImageDelivery) {
        if (previewUrl) {
            return previewUrl as T;
        }
        const match = fileRoutePattern.exec(url);
        if (match) {
            const [, route, shared, query = ""] = match;
            return `${route}${shared ? "/sharedPreview" : "/preview"}${query}` as T;
        }
    }
    // The generated BTFile model types friendlyFileName as nullable, and the Attached Files and Trash
    // directories really do return null, so a missing name must not throw while mapping a whole list.
    const extension = (fileNameOrExtension ?? "").split(".").pop() ?? "";
    return getViewerSource(url, extension).url as T;
}

export const mediaDataToSlide = (data: IMediaData): IBaseSlide => {
    if (data.type === "image360") {
        return {
            type: "custom-360-photo",
            metadata: {
                mediaId: data.mediaInfo?.documentInstanceId,
                jobId: data.mediaInfo?.jobId,
                dateAttached: data.dateAttached ?? undefined,
                addedBy: data.addedBy,
            },
            src: data.source,
            title: data.fileName,
            download: {
                url: data.source,
                filename: data.fileName,
            },
        };
    }

    if (data.type === "video") {
        const slide: IVimeoSlide = {
            type: "custom-video",
            metadata: {
                mediaId: data.mediaInfo?.documentInstanceId,
                jobId: data.mediaInfo?.jobId,
                dateAttached: data.dateAttached ?? undefined,
                addedBy: data.addedBy,
            },
            vimeoId: data.source,
            src: data.source,
            title: data.fileName,
        };

        return slide;
    }

    return {
        type: "image",
        metadata: {
            mediaId: data.mediaInfo?.documentInstanceId,
            jobId: data.mediaInfo?.jobId,
            dateAttached: data.dateAttached ?? undefined,
            addedBy: data.addedBy,
        },
        src: browserSafeImageSrc(data.sourceWithDrawings ?? data.source, data.fileName),
        title: data.fileName,
        download: {
            url: data.sourceWithDrawings ?? data.source,
            filename: data.fileName,
        },
    };
};

export const shouldAutoplayVideo = (offset: number, isSlideshowPlaying: boolean) =>
    !isSlideshowPlaying && offset === 0;

export const isShareableMedia = (slide: IBaseSlide) =>
    (slide.type === "image" || slide.type === "custom-360-photo") &&
    slide.metadata?.jobId !== undefined &&
    slide.metadata?.mediaId !== undefined;
